import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiCheck, FiX, FiCalendar, FiClock, FiChevronDown } from 'react-icons/fi';
import { DateTime } from 'luxon';
import apiService from '../../services/api';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';
import logger from '../../utils/logger';
import AttendanceCorrectionModal from '../attendance/AttendanceCorrectionModal';

const formatCheckedInTimeLabel = (value) => {
  if (!value) return null;
  const fromIso = DateTime.fromISO(value, { zone: 'utc' }).setZone('America/Chicago');
  if (fromIso.isValid) return fromIso.toFormat('LLL d, h:mm a');
  const fromSql = DateTime.fromSQL(value, { zone: 'utc' }).setZone('America/Chicago');
  if (fromSql.isValid) return fromSql.toFormat('LLL d, h:mm a');
  return null;
};

// A correction can only be requested once the check-in window for that day's
// session has closed. While the session is still active (check-in open), the
// student should check in normally rather than dispute.
const isCheckInWindowOver = (record) => {
  if (!record?.date) return true;
  const zone = 'America/Chicago';
  const datePart = String(record.date).slice(0, 10); // YYYY-MM-DD
  const now = DateTime.now().setZone(zone);
  if (record.end_time) {
    const endTime = String(record.end_time).slice(0, 8); // HH:MM:SS
    const windowEnd = DateTime.fromISO(`${datePart}T${endTime}`, { zone });
    if (windowEnd.isValid) return now > windowEnd;
  }
  // No end_time recorded: treat the window as over once the session day passes.
  const sessionDay = DateTime.fromISO(datePart, { zone });
  return sessionDay.isValid ? now.startOf('day') > sessionDay.startOf('day') : true;
};

const HandRaisedIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M8 12.5V5.5C8 4.67157 8.67157 4 9.5 4C10.3284 4 11 4.67157 11 5.5V11" stroke="#2f9e44" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11 11V4.5C11 3.67157 11.6716 3 12.5 3C13.3284 3 14 3.67157 14 4.5V11" stroke="#2f9e44" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 11V5.5C14 4.67157 14.6716 4 15.5 4C16.3284 4 17 4.67157 17 5.5V13" stroke="#2f9e44" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 12.5V8.5C17 7.67157 17.6716 7 18.5 7C19.3284 7 20 7.67157 20 8.5V15C20 18.3137 17.3137 21 14 21H12.5C11.1975 21 9.9366 20.5171 8.94202 19.7935C8.5 19.4 5 15 4.5 14.5C4 14 3.5 13 4.5 12.5C5.5 12 6.5 12.5 7 13L8 14" stroke="#2f9e44" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AttendanceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AttendanceStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 1rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 0.75rem;
    gap: 0.5rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => {
    switch (props.type) {
      case 'present': return 'var(--success-color)';
      case 'absent': return 'var(--error-color)';
      case 'rate': return 'var(--primary-color)';
      default: return 'var(--text-primary)';
    }
  }};
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const AttendanceList = styled.div`
  display: flex;
  flex-direction: column;
`;

const PendingReviewText = styled.span`
  color: var(--text-muted);
  font-size: 0.8rem;
  font-style: italic;
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: var(--text-muted);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  p {
    margin: 0;
    font-size: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    
    svg {
      width: 40px;
      height: 40px;
    }
    
    p {
      font-size: 0.875rem;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-top: 2px solid var(--primary-color);
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-muted);
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 0.75rem;
    font-size: 0.875rem;
  }
`;

/* ---------- Mobile accordion (responsive view) ---------- */
const MobileCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background-color: var(--background-primary);
  margin-bottom: 0.75rem;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MobileHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  background: none;
  border: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
`;

const MobileHeaderLeft = styled.div`
  min-width: 0;
`;

const MobileWeek = styled.div`
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 700;
  overflow-wrap: anywhere;
`;

const MobileDate = styled.div`
  color: var(--text-muted);
  font-size: 0.8rem;
  margin-top: 0.15rem;
`;

const MobileHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
`;

const MobilePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${props => props.$status === 'present' ? 'rgba(104, 211, 145, 0.12)' : 'rgba(252, 129, 129, 0.12)'};
  color: ${props => props.$status === 'present' ? 'var(--success-dark)' : 'var(--error-color)'};
  border: 1px solid ${props => props.$status === 'present' ? 'var(--success-color)' : 'var(--error-color)'};
`;

const MobileChevron = styled.span`
  display: inline-flex;
  justify-content: center;
  width: 18px;
  flex-shrink: 0;
  color: var(--text-muted);
  transition: transform 0.2s ease;
  transform: rotate(${props => (props.$expanded ? '180deg' : '0deg')});
`;

/* Fixed-width slot so the status pill (header) and the action/"I was here"
   (detail) share the same left edge in both. */
const StatusSlot = styled.div`
  width: 6.75rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
`;

const ChevronSpacer = styled.span`
  width: 18px;
  flex-shrink: 0;
`;

const MobileDetail = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--background-secondary);
`;

const MobileDetailText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: var(--text-muted);
  font-size: 0.8rem;
  min-width: 0;

  svg {
    flex-shrink: 0;
  }
`;

const IWasHereLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: none;
  border: none;
  padding: 0;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const IWasHereText = styled.span`
  text-decoration: underline;
  text-underline-offset: 2px;
`;

function CourseAttendanceRecords({ courseId, courseName, electiveId, electiveName, isElective = false }) {
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [correctionsBySession, setCorrectionsBySession] = useState({});
  const [submittingCorrection, setSubmittingCorrection] = useState(null);
  const [correctionModal, setCorrectionModal] = useState(null);
  const [correctionReason, setCorrectionReason] = useState('');
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [expandedSessions, setExpandedSessions] = useState({});

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleSession = (sessionId) => {
    setExpandedSessions((prev) => ({ ...prev, [sessionId]: !(prev[sessionId] ?? true) }));
  };

  const entityId = isElective ? electiveId : courseId;
  const entityName = isElective ? electiveName : courseName;

  const fetchAttendanceData = useCallback(async (id, isElective) => {
    try {
      setLoading(true);
      setError('');
      
      const response = isElective 
        ? await apiService.getStudentElectiveAttendance(id)
        : await apiService.getStudentAttendance(id);
      
      if (response.success) {
        setAttendanceData(response.attendance);
      } else {
        setError(response.message || 'Failed to fetch attendance data');
      }
    } catch (error) {
      logger.error('Error fetching attendance data', { error, entityId, isElective });
      setError('Failed to fetch attendance data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  // Fetch attendance data for the course or elective
  useEffect(() => {
    if (entityId) {
      fetchAttendanceData(entityId, isElective);
    }
  }, [entityId, isElective, fetchAttendanceData]);

  const fetchCorrections = useCallback(async () => {
    try {
      const response = await apiService.getMyAttendanceCorrections();
      const requests = Array.isArray(response?.requests) ? response.requests : [];
      const map = {};
      requests.forEach((req) => {
        if (req && req.session_id != null && !map[req.session_id]) {
          map[req.session_id] = req;
        }
      });
      setCorrectionsBySession(map);
    } catch (error) {
      logger.error('CourseAttendanceRecords fetch corrections failed', { error });
    }
  }, []);

  useEffect(() => {
    fetchCorrections();
  }, [fetchCorrections]);

  const submitCorrection = useCallback(async () => {
    if (!correctionModal?.sessionId) return;

    try {
      setSubmittingCorrection(correctionModal.sessionId);
      await apiService.requestAttendanceCorrection(correctionModal.sessionId, 'present', correctionReason.trim() || null);
      setCorrectionModal(null);
      setCorrectionReason('');
      await Promise.all([fetchCorrections(), fetchAttendanceData(entityId, isElective)]);
    } catch (error) {
      logger.error('CourseAttendanceRecords submit correction failed', { error, sessionId: correctionModal.sessionId });
      setError(error?.message || 'Failed to submit correction request.');
    } finally {
      setSubmittingCorrection(null);
    }
  }, [correctionModal, correctionReason, entityId, fetchAttendanceData, fetchCorrections, isElective]);


  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <FiCheck size={16} />;
      case 'absent':
        return <FiX size={16} />;
      default:
        return <FiClock size={16} />;
    }
  };

  // Calculate attendance statistics
  const calculateStats = () => {
    if (!attendanceData || !attendanceData.history) {
      return { presentCount: 0, absentCount: 0, totalSessions: 0, attendanceRate: 0 };
    }

    const history = attendanceData.history;
    const presentCount = history.filter(record => record.status === 'present').length;
    const absentCount = history.filter(record => record.status === 'absent').length;
    const totalSessions = history.length;
    const attendanceRate = totalSessions > 0 ? Math.round((presentCount / totalSessions) * 100) : 0;

    return { presentCount, absentCount, totalSessions, attendanceRate };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <LoadingState>
        <LoadingSpinner />
        <span>Loading attendance records...</span>
      </LoadingState>
    );
  }

  if (error) {
    return (
      <EmptyState>
        <FiX />
        <p>{error}</p>
      </EmptyState>
    );
  }

  return (
    <AttendanceWrapper>
      <AttendanceStats>
        <StatItem>
          <StatValue type="rate">{stats.attendanceRate}%</StatValue>
          <StatLabel>{isMobile ? 'Rate' : 'Attendance Rate'}</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue type="present">{stats.presentCount}</StatValue>
          <StatLabel>Present</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue type="absent">{stats.absentCount}</StatValue>
          <StatLabel>Absent</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.totalSessions}</StatValue>
          <StatLabel>{isMobile ? 'Total' : 'Total Sessions'}</StatLabel>
        </StatItem>
      </AttendanceStats>

      {attendanceData && attendanceData.history && attendanceData.history.length > 0 ? (
        <AttendanceList>
          {attendanceData.history.map((record) => {
            const isExpanded = expandedSessions[record.session_id] ?? true;
            const isPresent = record.status === 'present';
              return (
                <MobileCard key={record.session_id}>
                  <MobileHeader
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => toggleSession(record.session_id)}
                  >
                    <MobileHeaderLeft>
                      <MobileWeek>{record.title || `${entityName} Session`}</MobileWeek>
                      <MobileDate>{formatDateForDisplay(record.date, 'MMM d, yyyy')}</MobileDate>
                    </MobileHeaderLeft>
                    <MobileHeaderRight>
                      <StatusSlot>
                        <MobilePill $status={record.status}>
                          {getStatusIcon(record.status)}
                          {isPresent ? 'Present' : 'Absent'}
                        </MobilePill>
                      </StatusSlot>
                      <MobileChevron $expanded={isExpanded}>
                        <FiChevronDown size={18} />
                      </MobileChevron>
                    </MobileHeaderRight>
                  </MobileHeader>

                  {isExpanded && (
                    <MobileDetail>
                      {isPresent ? (() => {
                        const checkedInLabel = formatCheckedInTimeLabel(
                          record.checked_in_at_utc || record.last_checkin || record.updated_at
                        );
                        return (
                          <MobileDetailText>
                            <FiClock size={13} />
                            {checkedInLabel ? `Checked in ${checkedInLabel}` : 'Marked present'}
                          </MobileDetailText>
                        );
                      })() : (() => {
                        const correction = correctionsBySession[record.session_id];
                        const isPending = correction?.status === 'pending';
                        return (
                          <>
                            <MobileDetailText>No check-in recorded</MobileDetailText>
                            <MobileHeaderRight>
                              <StatusSlot>
                                {isPending ? (
                                  <PendingReviewText>Pending review</PendingReviewText>
                                ) : isCheckInWindowOver(record) ? (
                                  <IWasHereLink
                                    type="button"
                                    disabled={submittingCorrection === record.session_id}
                                    onClick={() => {
                                      setCorrectionReason('');
                                      setCorrectionModal({
                                        sessionId: record.session_id,
                                        title: record.title || `${entityName} Session`,
                                        date: record.date,
                                        currentStatus: record.status,
                                      });
                                    }}
                                  >
                                    <HandRaisedIcon size={14} />
                                    <IWasHereText>I was here</IWasHereText>
                                  </IWasHereLink>
                                ) : null}
                              </StatusSlot>
                              <ChevronSpacer />
                            </MobileHeaderRight>
                          </>
                        );
                      })()}
                    </MobileDetail>
                  )}
                </MobileCard>
              );
          })}
        </AttendanceList>
      ) : (
        <EmptyState>
          <FiCalendar />
          <p>No attendance records found for {entityName}.</p>
        </EmptyState>
      )}

      <AttendanceCorrectionModal
        isOpen={Boolean(correctionModal)}
        sessionTitle={correctionModal?.title}
        sessionDateLabel={correctionModal ? formatDateForDisplay(correctionModal.date, 'MMM d, yyyy') : ''}
        currentStatus={correctionModal?.currentStatus}
        requestedStatus="present"
        reason={correctionReason}
        onReasonChange={setCorrectionReason}
        submitting={Boolean(submittingCorrection)}
        onClose={() => setCorrectionModal(null)}
        onSubmit={submitCorrection}
      />
    </AttendanceWrapper>
  );
}

export default CourseAttendanceRecords;
